<template>
  <UDashboardGroup>
    <UDashboardSidebar collapsible resizable>

      <template #header="{ collapsed }">
        <UDropdownMenu
          :items="accounts"
          :content="{ align: 'center', collisionPadding: 12 }"
          :ui="{ content: collapsed ? 'w-40' : 'w-(--reka-dropdown-menu-trigger-width)' }"
        >
          <UButton
            v-bind="{
              ...activeAccount,
              label: collapsed ? undefined : activeAccount?.label,
              trailingIcon: collapsed ? undefined : 'i-ph-caret-down'
            }"
            :to="null"
            :avatar="{...activeAccount?.avatar, size: 'md'}"
            color="neutral"
            variant="ghost"
            block
            :square="collapsed"
            class="data-[state=open]:bg-elevated"
            :class="[!collapsed && 'py-2']"
            :ui="{
              trailingIcon: 'text-dimmed',
            }"
          />
        </UDropdownMenu>
      </template>

      <template #default="{ collapsed }">

        <div class="flex gap-1.5 flex-col">
          <slot name="sidebar" :collapsed="collapsed"/>
          <USeparator />
        </div>

        <div class="flex-1" />

        <UNavigationMenu
          :collapsed="collapsed"
          :items="footerLinks"
          orientation="vertical"
          class="mt-auto"
          tooltip
          popover
        />

        <UDashboardSearch v-if="showAdminTools" :loading="searchLoading" :groups="groups" :color-mode="false" v-model:search-term="searchTerm" />
      </template>

      <template #footer="{ collapsed }">
        <UDropdownMenu :items="items" :content="{ align: 'center', collisionPadding: 12 }"
                       :ui="{ content: collapsed ? 'w-48' : 'w-(--reka-dropdown-menu-trigger-width)' }">
          <UButton
            v-bind="{
              ...profile,
              label: collapsed ? undefined : profile.full_name,
              trailingIcon: collapsed ? undefined : 'i-ph-dots-three-vertical'
            }"
            color="neutral"
            variant="ghost"
            block
            :square="collapsed"
            class="data-[state=open]:bg-elevated"
            :ui="{
              trailingIcon: 'text-dimmed'
            }"
          >
            <template #leading>
              <UChip inset v-if="profile.impersonate" color="admin">
                <UAvatar :label="profile.full_name" :src="profile.avatar_converted_url ? profile.avatar_converted_url : '/im/user-avatar-default.png'" size="sm"></UAvatar>
              </UChip>
              <UAvatar v-else :label="profile.full_name" :src="profile.avatar_converted_url ? profile.avatar_converted_url : '/im/user-avatar-default.png'" size="sm"></UAvatar>
            </template>
          </UButton>

          <template #account>
            <div class="text-left w-full opacity-90">
              <p>
                {{profile.impersonate ? 'Usuraption du compte' : 'Connecté avec le mail'}}
              </p>
              <p class="truncate font-medium text-default">
                {{profile.email}}
              </p>
            </div>
          </template>

          <template #admin>
            <div class="w-full opacity-100">
              <USwitch v-model="adminTools" color="admin" label="Outils d'admin" />
            </div>
          </template>
        </UDropdownMenu>
      </template>
    </UDashboardSidebar>
    <slot />
  </UDashboardGroup>
</template>

<script setup>
import { useStorage } from '@vueuse/core'
import ModalHelp from '../components/ModalHelp.vue'

const route = useRoute()
const config = useRuntimeConfig()

const appUrl = 'https://www.' + config.public.domain;

const profile = useState('profile');
const { refresh } = await useApi(appUrl + '/api/user/me', {
  onResponse({ request, response, options }) {
    profile.value = response._data.user
  },
  immediate: false,
})

const { on } = useEventBus('refreshGroup');
on(refresh)

const adminTools = useStorage('adminTools', () => true)
const isAdmin = useState('isAdmin')
isAdmin.value = computed(() => (profile.value.account_type === 'admin' || (profile.value.impersonate && profile.value.initial_user.account_type === 'admin')) && adminTools.value)
const showAdminTools = computed(() => profile.value.account_type === 'admin' || (profile.value.impersonate && profile.value.initial_user.account_type === 'admin'))

const adminAccount = computed(() => {
  return {
    label: 'Admin',
    avatar: {
      alt: 'Admin',
      class: 'rounded-none squircle',
    },
    to: `/admin`,
    class: profile.value.impersonate ? 'text-admin' : '',
  }
})

function organizationAccount(o) {
  if (!o) {
    return null
  }
  return {
    label: o.name,
    avatar: {
      src: o.avatar_converted_url ? o.avatar_converted_url : '/im/organization-avatar-default.png',
      class: 'rounded-none squircle',
    },
    to: `${appUrl}/dashboard/${o.id}`,
    external: true,
  }
}

function groupAccount(g) {
  if (!g) {
    return null
  }
  return {
    label: g.name,
    avatar: {
      src: g.avatar_converted_url ? g.avatar_converted_url : '/im/organization-avatar-default.png',
      class: 'rounded-none squircle',
    },
    to: `/group/${g.id}`
  }
}

function platformAccount(p) {
  if (!p) {
    return null
  }
  return {
    label: p.name,
    avatar: {
      src: appUrl + '/assets/im/platform/' + p.slug + '-appleicon.png',
      class: 'rounded-none squircle',
    },
    to: `/platform/${p.id}`
  }
}

const accounts = computed(() => {

  let accounts = [];

  if (profile.value.account_type === 'organization') {
    accounts.push((profile.value.groups.length || profile.value.platforms.length ? [{
      label: 'Structure' + (profile.value.organizations.length > 1 ? 's' : ''),
      disabled: true
    }] : []).concat(profile.value.organizations.map((g) => organizationAccount(g))).concat({
      icon: 'i-ph-plus-circle-duotone',
      label: 'Créer une structure',
      to: '/organization/new',
    }))
  }

  if (profile.value.groups.length) {
    accounts.push([{
      label: 'Réseau' + (profile.value.groups.length > 1 ? 'x' : ''),
      disabled: true
    }].concat(profile.value.groups.map((g) => groupAccount(g))))
  }

  if (profile.value.platforms.length) {
    accounts.push([{
      label: 'Plateforme' + (profile.value.platforms.length > 1 ? 's' : ''),
      disabled: true
    }].concat(profile.value.platforms.map((p) => platformAccount(p))))
  }

  if (isAdmin.value) {
    accounts.push([adminAccount.value])
  }

  return accounts;
})

const currentGroup = useState('group')
const currentPlatform = useState('platform')
const currentOrganization = useState('organization')

const activeAccount = computed(() => {
  if (route.path.startsWith('/admin')) {
    return adminAccount.value
  }
  if (route.path.startsWith('/group')) {
    let userGroup = profile.value.groups.find((g) => g.id === route.params.id)
    if (!userGroup) {
      userGroup = currentGroup.value
    }
    return groupAccount(userGroup)
  }
  if (route.path.startsWith('/platform')) {
    let userPlatform = profile.value.platforms.find((p) => p.id === route.params.id)
    if (!userPlatform) {
      userPlatform = currentPlatform.value
    }
    return platformAccount(userPlatform)
  }
  if (route.path.startsWith('/organization')) {
    let userOrganization = profile.value.organizations.find((o) => o.id === route.params.id)
    if (!userOrganization) {
      userOrganization = currentOrganization.value
    }
    return organizationAccount(userOrganization)
  }
})

const items = computed(() => {

  let accountItems = [{
    slot: 'account',
    label: '',
    disabled: true
  }]
  if (showAdminTools.value) {
    accountItems.push({
      slot: 'admin',
        label: '',
        disabled: true
    })
  }
  if (profile.value.impersonate) {
    accountItems.push({
      label: 'Désusurper',
      icon: 'i-ph-eye-slash-duotone',
      onSelect: async () => {
        await $api('/auth/impersonate', {
          method: 'DELETE',
        })
        await refresh()
      }
    })
  } else {
    accountItems.push({
      label: 'Se déconnecter',
      icon: 'i-ph-sign-out',
      onSelect: async () => {
        navigateTo(appUrl + '/dashboard?logout' + (profile.value.account_type !== 'volunteer' ? '&account=organization' : ''), {external: true})
      }
    })
  }

  return [
    accountItems,
    [{
    label: 'Paramètres',
    icon: 'i-ph-gear-duotone',
    target: '_blank',
    to: appUrl + '/settings',
    rel: ''
  }, {
    label: `Passer en ${isDark.value ? 'light' : 'dark'} mode`,
    icon: isDark.value ? appConfig.ui.icons.light : appConfig.ui.icons.dark,
    onSelect: () => {
      isDark.value = !isDark.value
    }
  }]
  ]
})

const colorMode = useColorMode()
const appConfig = useAppConfig()

// Computed

const isDark = computed({
  get () {
    return colorMode.value === 'dark'
  },
  set () {
    colorMode.preference = colorMode.value === 'dark' ? 'light' : 'dark'
  }
})

const searchTerm = ref('')
const searchTermDebounced = refDebounced(searchTerm, 200)
const searchLoading = computed(() =>
  searchGroupsStatus.value === 'pending'
  || searchOrganizationsStatus.value === 'pending'
  || searchUsersStatus.value === 'pending'
  || searchPlatformsStatus.value === 'pending'
)

const searchTermGroups = computed(() => `type:group ${searchTermDebounced.value}`)
const { data: searchGroups, status: searchGroupsStatus, execute: executeSearchGroups } = await useApi('/admin/search', {
  query: {
    query: searchTermGroups,
    per_page: 5,
  },
  immediate: false,
  lazy: true,
  transform: (data) => {
    const result = data.data.map(function(item) {
      return {
        id: item.id,
        label: item.name,
        suffix: item.owner ? item.owner.full_name : '',
        avatar: {
          src: item.network?.avatar_converted_url ? item.network.avatar_converted_url : '/im/organization-avatar-default.png',
          class: 'rounded-none squircle',
        },
        to: '/group/' + item.id,
      }
    })

    if (data.count.all > result.length) {
      result.push({
        id: 'group-more',
        label: 'Voir plus',
        to: `/admin/group?q=${searchTermDebounced.value}`
      })
    }

    return result
  },
})
watch(searchTermGroups, () => executeSearchGroups(), { once: true })

const searchTermOrganizations = computed(() => `type:organization ${searchTermDebounced.value}`)
const { data: searchOrganizations, status: searchOrganizationsStatus, execute: executeSearchOrganizations } = await useApi('/admin/search', {
  query: {
    query: searchTermOrganizations,
    per_page: 5,
  },
  immediate: false,
  lazy: true,
  transform: (data) => {
    const result = data.data.map(function(item) {
      return {
        id: `org-${item.id}`,
        label: item.name,
        suffix: item.owner?.full_name,
        avatar: {
          src: item.avatar_converted_url ? item.avatar_converted_url : '/im/organization-avatar-default.png',
          chip: {
            color: item.status !== 'active' ? 'error' : null,
            position: 'bottom-right',
            inset: true,
          },
          class: 'rounded-none squircle',
        },
        to: appUrl + '/organization/' + item.id,
        rel: '',
      }
    })

    if (data.count.all > result.length) {
      result.push({
        id: 'org-more',
        label: 'Voir plus',
        to: `/admin/organization?q=${searchTermDebounced.value}`
      })
    }

    return result
  },
})
watch(searchTermOrganizations, () => executeSearchOrganizations(), { once: true })

const searchTermUsers = computed(() => `type:user ${searchTermDebounced.value}`)
const { data: searchUsers, status: searchUsersStatus, execute: executeSearchUsers } = await useApi('/admin/search', {
  query: {
    query: searchTermUsers,
    per_page: 5,
  },
  immediate: false,
  lazy: true,
  transform: (data) => {
    const result = data.data.map(function(item) {
      return {
        id: `user-${item.id}`,
        label: item.full_name,
        suffix: item.email + ' (' + (item.account_type === 'organization' ? 'Espace Asso' : (item.account_type === 'admin' ? 'Admin' : item.platform.organization_name)) + ')',
        avatar: {
          src: item.avatar_converted_url ? item.avatar_converted_url : '/im/user-avatar-default.png',
          chip: {
            color: item.status !== 'active' ? 'error' : null,
            position: 'bottom-right',
          },
        },
        to: appUrl + '/settings/' + item.id,
        rel: '',
      }
    })

    if (data.count.all > result.length) {
      result.push({
        id: 'user-more',
        label: 'Voir plus',
        to: `/admin/user?q=${searchTermDebounced.value}`
      })
    }

    return result
  },
})
watch(searchTermUsers, () => executeSearchUsers(), { once: true })

const { data: searchPlatforms, status: searchPlatformsStatus, execute: executeSearchPlatforms } = await useApi('/platform', {
  query: {
    query: searchTermDebounced,
    per_page: 5,
  },
  immediate: false,
  lazy: true,
  transform: (data) => {
    const result = data.data.map(function(item) {
      return {
        id: item.id,
        label: item.name,
        suffix: item.organization_name,
        avatar: {
          src: appUrl + '/assets/im/platform/' + item.slug + '-appleicon.png',
          class: 'rounded-none squircle',
        },
        to: `/platform/${item.id}`,
        rel: '',
      }
    })

    if (data.total > result.length) {
      result.push({
        id: 'platform-more',
        label: 'Voir plus',
        to: `/admin/platform`
      })
    }

    return result
  },
})
watch(searchTermDebounced, () => executeSearchPlatforms(), { once: true })

const groups = computed(() =>[{
  id: 'groups',
  label: 'Réseaux',
  items: searchGroups.value?.map(i => {
    i.onSelect = (e) => {
      if (e.detail.originalEvent.shiftKey) {
        e.preventDefault()
      }
    }
    return i
  }) || [],
  ignoreFilter: true,
}, {
  id: 'organizations',
  label: 'Structures',
  items: searchOrganizations.value?.map(i => {
    i.onSelect = (e) => {
      if (e.detail.originalEvent.shiftKey) {
        e.preventDefault()
      }
    }
    return i
  }) || [],
  ignoreFilter: true,
}, {
  id: 'users',
  label: 'Utilisateurs',
  items: searchUsers.value?.map(i => {
    i.onSelect = (e) => {
      if (e.detail.originalEvent.shiftKey) {
        e.preventDefault()
      }
    }
    return i
  }) || [],
  ignoreFilter: true,
}, {
  id: 'platforms',
  label: 'Plateformes',
  items: searchPlatforms.value?.map(i => {
    i.onSelect = (e) => {
      if (e.detail.originalEvent.shiftKey) {
        e.preventDefault()
      }
    }
    return i
  }) || [],
  ignoreFilter: true,
}]);

const helpModal = useOverlay().create(ModalHelp)

const footerLinks = [{
  label: 'Aide & Support',
  icon: 'i-ph-lifebuoy-duotone',
  onSelect: () => helpModal.open()
}]
</script>
